/* eslint-disable jsx-a11y/control-has-associated-label */
import {LetrusApi} from '@letrustech/letrus-api-interfaces';
import Annotations from 'components/Annotations';
import AsideProposal from 'components/AsideProposal';
import BasicEditor from 'components/BasicEditor';
import EditorHeader from 'containers/EditorPage/components/CompositionHeader';
import PageLoading from 'components/PageLoading';
import ReviewSidebar from 'components/ReviewSidebar';
import SidebarButtons from 'components/SidebarButtons';
import StatisticsSidebar, {
  CompositionProps,
} from 'components/StatisticsSidebar';
import TooltipOverlay from 'components/TooltipOverlay';
import domAlign from 'dom-align';
import useCompositionNavegation from 'features/useCompositionNavegation';
import {fromJS} from 'immutable';
import {LineWordCounter} from 'letrus-ui';
import {ContentWrapper} from 'components/ui';
import Tooltip from 'rc-tooltip';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {
  clearParentOrChildComposition,
  fetchCompositionByIdRequest,
  fetchCompositionJsonStatsAsyncRequest,
  getCompositionById,
  getCompositionFetchError,
  getCompositionJsonStatsAsync,
  getIsLoadingCompositionById,
  getParentOrChildCompositionById,
  isLoadingParentOrChildCompositionById,
  setCompositionEditorState,
  setCompositionLocalState,
  setHighlight,
} from 'store/reducers/compositions';
import {
  fetchLearningPathStepByOrderRequest,
  getLearningPath,
  getLearningPathStep,
} from 'store/reducers/learningPath';
import {
  changeToggleCompletedTestReportStatus,
  getToggleStatus,
  SelectedToggle,
} from 'store/reducers/toggle';

import {ApplicationState} from 'store/rootReducer';
import {getCompositionTotalOfLines} from 'utils/functions/CompositionLineCounter';
import countWords from 'utils/functions/count-words';
import isEmpty from 'utils/functions/isEmpty';
import {reviveEditorState} from 'utils/functions/reviveEditorState';
import {useMobile, usePrevious, useQueryParam} from 'utils/hooks';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {CompositionDetailExtended} from 'utils/types/compositions';
import {fetchWritePreparation} from 'APIs/letrusAPI/writePreparation';
import {updateHasSeenCompositionRequest} from 'APIs/letrusAPI/compositionReview';
import TooltipModal from './TooltipModal';
import NextSteps from './NextSteps';
import './ReviewedComposition.scss';

interface StateProps {
  toggleStatus: SelectedToggle;
  composition: ImmutableMap<CompositionProps>;
  jsonStatsAsync: ImmutableMap<LetrusApi.JsonStatsAsyncResponse>;
  isLoadingCompositionById: boolean;
  isLoadingParentOrChildCompositionById: boolean;
  parentOrChildComposition: ImmutableMap<CompositionDetailExtended>;
  compositionFetchError: boolean;
}

interface DispatchProps {
  fetchCompositionByIdRequest: typeof fetchCompositionByIdRequest;
  fetchCompositionJsonStatsAsyncRequest: typeof fetchCompositionJsonStatsAsyncRequest;
  setCompositionLocalState: typeof setCompositionLocalState;
  setHighlight: typeof setHighlight;
  clearParentOrChildComposition: typeof clearParentOrChildComposition;
  setCompositionEditorState: typeof setCompositionEditorState;
  changeToggleCompletedTestReportStatus: typeof changeToggleCompletedTestReportStatus;
}

export type ReviewedCompositionProps = DispatchProps & StateProps;

function ReviewedComposition({
  toggleStatus,
  composition,
  isLoadingCompositionById,
  jsonStatsAsync,
  fetchCompositionByIdRequest,
  fetchCompositionJsonStatsAsyncRequest,
  isLoadingParentOrChildCompositionById,
  setCompositionLocalState,
  clearParentOrChildComposition,
  setCompositionEditorState,
  parentOrChildComposition,
  setHighlight,
  changeToggleCompletedTestReportStatus,
  compositionFetchError,
}: ReviewedCompositionProps): JSX.Element {
  const [reviewNodeState, setReviewNodeState] =
    useState<HTMLDivElement | null>();
  const [fullSidebar, setFullSidebar] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>();
  const [tooltipAnnotation, setTooltipAnnotation] = useState<
    ImmutableMap<LetrusApi.ReviewAnnotation>
  >(fromJS({}));
  const [tooltipElemClassName, setTooltipElemClassName] = useState();
  const [scheduledCloseTooltipId, setScheduledCloseTooltipId] =
    useState<null | NodeJS.Timeout>(null);
  const [sideReportInitialCountTime, setSideReportInitialCountTime] =
    useState(0);
  const [visibleAnottation, setVisibleAnotattion] =
    useState<ImmutableMap<LetrusApi.ReviewAnnotation> | undefined>();

  const navigate = useNavigate();
  const {composition_id} = useParams();
  const updateHasSeenComposition = updateHasSeenCompositionRequest();
  const writePreparation = fetchWritePreparation(composition_id, {
    isPaused: () => !composition.get('uses_write_preparation'),
  });

  const isLoading =
    isLoadingCompositionById ||
    isLoadingParentOrChildCompositionById ||
    writePreparation.isLoading;

  const previousStatus = usePrevious(toggleStatus);
  const {trackEvent} = useTrackEvent();
  const previousIsLoadingCompositionById = usePrevious(
    isLoadingCompositionById,
  );
  const {isMobile} = useMobile();
  const {redirectCompositionByStatus} = useCompositionNavegation({composition});
  const queryParams = useQueryParam();
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';

  const compositionTitle = isEmpty(composition?.get('title'))
    ? 'Redação sem título'
    : composition?.get('title');
  const currentCompositionId = Number(composition?.get('id'));
  const requestedCompositionId = Number(composition_id);
  const compositionRaw = composition?.get('composition_raw');

  const wordsNumber = isMobile
    ? undefined
    : countWords(composition.get('composition_raw'));

  const linesNumber = getCompositionTotalOfLines(
    composition.get('composition_raw'),
  );

  const review = composition.get('reviews')?.first();

  const isFundII =
    composition.getIn(['genre', 'review_grid_name']) === 'Grade Única';

  const schoolGradeName = composition.getIn([
    'test',
    'school_group_grade_name',
  ]);

  const isIaComp = composition?.get('review_type') === 'AI-Competences';

  const compositionGenre = composition.get('genre') || fromJS({});
  const visibleAnottationCompetence = compositionGenre
    ?.get('competences')
    ?.find((c: any) => c.get('id') === visibleAnottation?.get('competence'));

  const isReviewGridMultigenre =
    composition.getIn(['instruction_id', 'genre', 'review_grid']) === 61;

  // This cycle type student is being used for teacher view
  const cycleType =
    isFundII && schoolGradeName
      ? schoolGradeName.includes('6') || schoolGradeName.includes('7')
        ? 1
        : 2
      : 2;

  const reviewCompositionId = composition?.get('reviews')?.first()?.get('id');

  const wasSeenByStudent = composition
    ?.get('reviews')
    ?.first()
    ?.get('seen_by_student');

  const eventData: EventData = {
    test: {
      test_id: composition?.get('test_id') ?? 0,
    },
    composition: {
      composition_id: Number(composition?.get('id')) ?? 0,
    },
    review: {
      review_id: Number(composition?.get('reviews')?.first()?.get('id') ?? 0),
    },
    learningPathUserInstance: {
      learning_path_user_instance_id: Number(
        composition?.get('learning_path_user_instance_id'),
      ),
      step_id: composition?.getIn(['step', 'id']),
    },
  };

  useEffect(() => {
    trackEvent('has_seen_composition_feedback', eventData);
  }, []);

  useEffect(() => {
    if (isMobile) {
      changeToggleCompletedTestReportStatus(null);
      return;
    }
    if (jsonStatsAsync.size) {
      changeToggleCompletedTestReportStatus(
        isFundII ? 'correction' : 'structure',
      );
      return;
    }
    changeToggleCompletedTestReportStatus('proposal');
  }, [jsonStatsAsync?.size]);

  useEffect(() => {
    if (previousIsLoadingCompositionById && !isLoadingCompositionById) {
      if (composition?.size) {
        setSideReportInitialCountTime(new Date().getTime());

        return redirectCompositionByStatus();
      }
    }
  }, [isLoadingCompositionById, previousIsLoadingCompositionById]);

  // Transfer the previous fetched parent or child composition state to the editor state
  // and then clean it to prevent duplicate states
  useEffect(() => {
    fetchTriggers();

    if (
      parentOrChildComposition?.size &&
      parentOrChildComposition?.get('id') === Number(composition_id)
    ) {
      setCompositionLocalState(parentOrChildComposition);
      setCompositionEditorState(
        reviveEditorState(parentOrChildComposition.toJS()),
      );
      clearParentOrChildComposition();
    } else {
      fetchAll();
    }
  }, [composition_id]);

  useEffect(() => {
    window.addEventListener('beforeunload', onUnload);

    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [sideReportInitialCountTime, toggleStatus]);

  useEffect(() => {
    if (toggleStatus === previousStatus) {
      if (toggleStatus === 'competencies' || toggleStatus === 'structure') {
        trackTimeSpentOnReviewTabsEvent(toggleStatus);
      }
      return;
    }
    if (previousStatus === 'structure' || previousStatus === 'competencies') {
      trackTimeSpentOnReviewTabsEvent(previousStatus);
    }

    setSideReportInitialCountTime(new Date().getTime());

    if (
      (toggleStatus === 'competencies' || toggleStatus === 'structure') &&
      isIaComp &&
      composition.get('reviews')?.size
    ) {
      trackEvent('passed_between_structure_and_ia_report', eventData);
    }
  }, [toggleStatus]);

  useEffect(() => {
    const hasLoadedRequestedComposition =
      composition.size && currentCompositionId === requestedCompositionId;

    if (
      hasLoadedRequestedComposition &&
      compositionGenre &&
      previousIsLoadingCompositionById &&
      !isLoadingCompositionById &&
      compositionRaw
    ) {
      const shownStatsMap: any =
        composition && compositionGenre && compositionGenre.get('shown_stats')
          ? compositionGenre.get('shown_stats')
          : [];

      fetchCompositionJsonStatsAsyncRequest(composition.toJS(), {
        compositionRaw,
        processComments: true,
        markersToProcess: shownStatsMap.map((shownStat) => {
          if (!shownStat.size) return;
          if (!shownStat.get('marker')) return;

          return shownStat.getIn(['marker', 'varname']);
        }),
      });
    }

    if (reviewCompositionId && !wasSeenByStudent) {
      updateHasSeenComposition.trigger({id: reviewCompositionId});

      trackEvent('test_review_not_seen_notification');
    }
  }, [
    composition,
    compositionGenre,
    compositionRaw,
    composition_id,
    currentCompositionId,
    previousIsLoadingCompositionById,
    isLoadingCompositionById,
    requestedCompositionId,
  ]);

  function fetchTriggers() {
    fetchAll();

    if (!composition.size) {
      return;
    }

    if (
      !learningPathUserInstanceId &&
      !isLoadingCompositionById &&
      (!composition.get('flag_revision') ||
        composition.get('status') === 'teacher revision pending')
    ) {
      navigate(`/atividades/escrita/${composition.get('id')}`, {replace: true});
    }

    // Code for questions of hotjar when isFundII
  }

  function trackTimeSpentOnReviewTabsEvent(tabName: string) {
    const reviewTabEndCountTime = new Date().getTime();
    const timeSpent = reviewTabEndCountTime - sideReportInitialCountTime;
    const eventName =
      tabName === 'competencies'
        ? 'time_spent_on_review_tab'
        : 'time_spent_on_structure_indicators_tab';

    trackEvent(eventName, {
      ...eventData,
      time: {
        spent_time: timeSpent,
      },
      test: {
        test_id: composition.get('test_id'),
      },
      composition: {
        composition_id: Number(composition.get('id')),
      },
      review: {
        review_id: Number(composition.get('reviews')?.first()?.get('id') ?? 0),
      },
      learningPathUserInstance: {
        learning_path_user_instance_id: Number(
          composition?.get('learning_path_user_instance_id'),
        ),
        step_id: composition?.getIn(['step', 'id']) ?? 0,
      },
    });
  }

  function onUnload(event: BeforeUnloadEvent) {
    if (toggleStatus === 'competencies' || toggleStatus === 'structure') {
      event.preventDefault();

      trackTimeSpentOnReviewTabsEvent(toggleStatus);

      setSideReportInitialCountTime(new Date().getTime());

      const confirmationMessage = 'Tem certeza que deseja sair?';
      event.returnValue = confirmationMessage;

      return confirmationMessage;
    }
  }

  function onMouseLeave() {
    cancelCloseTooltip();
    scheduleCloseTooltip();
  }

  function onMouseOverAnnotation(event) {
    const klass = event.target.id || event.target.className.split(' ')[1];
    const competenceClass = event.target.className.split(' ')[0];

    if (
      competenceClass &&
      (competenceClass.includes('full-text') ||
        competenceClass.includes('full-paragraph'))
    ) {
      return onMouseLeave();
    }
    if (!klass) {
      return onMouseLeave();
    }

    const annotation: any =
      review
        .get('annotations')
        .find(
          (anotattion: any) =>
            anotattion.get('id') === +klass.slice('comment-'.length),
        ) ||
      review
        .get('general_annotations')
        .find(
          (anotattion) =>
            anotattion.get('id') === +klass.slice('comment-'.length),
        ) ||
      review
        .get('general_annotations')
        .find(
          (anotattion) =>
            anotattion.get('id') === +klass.slice('general-comment-'.length),
        );

    if (!annotation) {
      return onMouseLeave();
    }

    cancelCloseTooltip();

    setTooltipVisible(true);
    setTooltipAnnotation(fromJS(annotation));
    setTooltipElemClassName(event.target.className.split(' ')[0]);

    return false;
  }

  function closeTooltip() {
    setTooltipVisible(false);
    setTooltipAnnotation(fromJS({}));
  }

  function cancelCloseTooltip() {
    if (scheduledCloseTooltipId) {
      clearTimeout(scheduledCloseTooltipId);
      return;
    }
    clearTimeout(undefined);
  }

  function scheduleCloseTooltip() {
    const timeout = setTimeout(closeTooltip, 400);
    setScheduledCloseTooltipId(timeout as any);
  }

  function alignWithSpan(node) {
    const annotation = tooltipAnnotation;

    if (annotation && reviewNodeState) {
      const commentNode = reviewNodeState.querySelector(
        `.comment-${annotation.get('id')}, #general-comment-${annotation.get(
          'id',
        )}`,
      );

      if (!commentNode) {
        return;
      }

      if (!isMobile) {
        domAlign(node, commentNode, {
          points: ['br', 'tl'],
          overflow: {
            adjustX: 1,
            adjustY: 1,
          },
          offset: [4, 0],
          targetOffset: [0, 0],
        });
      } else {
        setVisibleAnotattion(tooltipAnnotation);
      }
    }
  }

  function fetchAll() {
    fetchCompositionByIdRequest(composition_id);
  }

  function getCompetenceGrade(competenceId: string | number) {
    const competence = compositionGenre
      .get('competences')
      .find((c: any) => c.get('id') === competenceId);

    const reviewGradeCompetence = review
      .get('review_grades')
      .find((review_grade) => review_grade.get('competence') === competenceId);

    const competenceGradeItemId = reviewGradeCompetence
      ? reviewGradeCompetence.getIn(['grade_item', 'id'])
      : '';

    return competence && competenceGradeItemId
      ? competence
          .get('grade_items')
          .toJS()
          .find((grade) => grade.id === competenceGradeItemId).display_points
      : '';
  }

  function getDisplayPoints(displayPoints: string, maxScore: number) {
    if (parseInt(displayPoints, 10)) {
      return `${displayPoints} de ${maxScore}`;
    }
    return displayPoints;
  }

  function renderTooltipOverlay() {
    const anotattion = tooltipAnnotation || fromJS({});
    if (anotattion) {
      const className = tooltipElemClassName;
      const competenceId = anotattion && anotattion.get('competence');
      // Treat general annotation case
      if (!competenceId) {
        return (
          <div
            onMouseEnter={cancelCloseTooltip}
            onMouseLeave={scheduleCloseTooltip}
          >
            <TooltipOverlay
              className={`TooltipOverlay--${className}`}
              message={anotattion && anotattion.get('comment')}
              entity={anotattion.toJS()}
            />
          </div>
        );
      }

      const competence = compositionGenre
        .get('competences')
        .find((c: any) => c.get('id') === competenceId);

      const competenceName = competence ? competence.get('name') : '';
      const reviewGradeCompetence = review
        .get('review_grades')
        .find(
          (review_grade) => review_grade.get('competence') === competenceId,
        );

      const competenceGradeItemId = reviewGradeCompetence
        ? reviewGradeCompetence.getIn(['grade_item', 'id'])
        : '';

      const competenceGrade =
        competence && competenceGradeItemId
          ? competence
              .get('grade_items')
              .toJS()
              .find((grade) => grade.id === competenceGradeItemId)
              .display_points
          : '';

      const competenceMaxValue: any = competence
        ? competence.get('max_value_range')
        : '';

      if (
        anotattion.get('suggested_text') &&
        /<p class="[^"]+">(\s|<br\s*\/?>)*<\/p>/.test(
          anotattion.get('suggested_text') as string,
        )
      ) {
        anotattion.set('suggested_text', '');
      }

      if (
        anotattion.get('comment') &&
        /<p class="[^"]+">(\s|<br\s*\/?>)*<\/p>/.test(
          anotattion.get('comment') as string,
        )
      ) {
        anotattion.set('comment', '');
      }

      const gradeMessage = isFundII
        ? ''
        : `${getDisplayPoints(competenceGrade, competenceMaxValue)} pts`;

      // When the mouse enters the tooltips we want it to stay open. Since the
      // tooltip target is on a raw HTML, non-react controlled part of the DOM,
      // rc-tooltip won't do this for us
      if (anotattion) {
        return (
          <div
            onMouseEnter={cancelCloseTooltip}
            onMouseLeave={scheduleCloseTooltip}
          >
            <TooltipOverlay
              className={`TooltipOverlay--${className}`}
              message={anotattion.get('comment')}
              competence={competenceName}
              score={gradeMessage}
              entity={anotattion.toJS()}
            />
          </div>
        );
      }
    }
  }

  function toggleFullsideBar() {
    setFullSidebar(!fullSidebar);
  }

  return !isLoading || compositionFetchError ? (
    <>
      <EditorHeader
        isCompositionFinished
        childId={composition.get('child_id')}
        parentId={composition.get('parent_id')}
        testName={composition.getIn(['test', 'name'])}
        isReviewGridMultigenre={isReviewGridMultigenre}
        learningPathUserInstanceId={learningPathUserInstanceId}
        usesAiComp={composition.getIn(['test', 'uses_ai_comp'])}
      />
      <div className="ContentWrapper">
        <ContentWrapper
          bodyContent={
            <div className="Reviewed">
              <div className="Content">
                {composition && (
                  <div className="Editor">
                    <SidebarButtons composition={composition} />
                    <LineWordCounter
                      amountOfWords={wordsNumber}
                      amountOfLines={linesNumber}
                    />
                    {composition && toggleStatus !== 'structure' && (
                      <div className="Editor">
                        <h2 className="CompositionTitle">{compositionTitle}</h2>

                        <div id="text_editor">
                          <Tooltip
                            onPopupAlign={alignWithSpan}
                            visible={tooltipVisible}
                            overlay={renderTooltipOverlay()}
                          >
                            <div className="DraftEditor-root">
                              <div className="DraftEditor-editorContainer">
                                <div
                                  className="public-DraftEditor-content"
                                  onFocus={onMouseOverAnnotation}
                                  onMouseOver={onMouseOverAnnotation}
                                  onMouseLeave={onMouseLeave}
                                  onClick={onMouseOverAnnotation}
                                  onKeyPress={onMouseOverAnnotation}
                                  role="button"
                                  tabIndex={0}
                                  ref={(reviewNode) => {
                                    setReviewNodeState(reviewNode);
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: review?.size
                                      ? review.get('composition_markup_html')
                                      : '',
                                  }}
                                />
                              </div>
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                    {composition && toggleStatus === 'structure' && (
                      <BasicEditor
                        compositionId={composition.get('id')}
                        compositionIsLoading={isLoadingCompositionById}
                        compositionRaw={composition.get('composition_raw')}
                        compositionTitle={compositionTitle}
                        disabled
                        linesNumber={linesNumber}
                        setCompositionLocalState={() => undefined}
                        wordsNumber={wordsNumber}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          }
          onExitClick={() => {
            changeToggleCompletedTestReportStatus(null);
          }}
          sidebarContent={
            <div className="ReviewedComposition__SidebarContent">
              {toggleStatus === 'proposal' && (
                <AsideProposal
                  composition={composition as any}
                  writePreparation={writePreparation.data}
                  isLoading={false}
                  setHasScrolledToEnd={() => {}}
                />
              )}

              {toggleStatus === 'structure' && (
                <StatisticsSidebar
                  composition={composition}
                  wordsNumber={wordsNumber}
                  jsonStatsAsync={jsonStatsAsync}
                  setHighlight={setHighlight}
                  hideCircularProgressBar
                />
              )}

              {toggleStatus === 'correction' && (
                <ReviewSidebar
                  genre={compositionGenre}
                  review={review}
                  isFundII={isFundII}
                  cycleType={cycleType}
                  toggleFullsideBar={toggleFullsideBar}
                />
              )}

              {toggleStatus === 'next-steps' && (
                <NextSteps>{review?.get('general_comments')}</NextSteps>
              )}

              {toggleStatus === 'competencies' && (
                <ReviewSidebar
                  genre={compositionGenre}
                  review={review}
                  isFundII={isFundII}
                  cycleType={cycleType}
                  toggleFullsideBar={toggleFullsideBar}
                  composition={composition}
                />
              )}

              {toggleStatus === 'annotations' &&
                composition.get('uses_write_preparation') && (
                  <Annotations writePreparation={writePreparation.data} />
                )}
            </div>
          }
          status={
            toggleStatus === null ? 'closed' : fullSidebar ? 'full' : 'open'
          }
        />
        {visibleAnottation && isMobile && (
          <TooltipModal
            isOpen
            onClose={() => setVisibleAnotattion(undefined)}
            anotattion={visibleAnottation}
            className={tooltipElemClassName || ''}
            competenceName={visibleAnottationCompetence?.get('name')}
            gradeMessage={
              !visibleAnottationCompetence || isFundII
                ? ''
                : `${getDisplayPoints(
                    getCompetenceGrade(visibleAnottation.get('competence')),
                    visibleAnottationCompetence.get('max_value_range'),
                  )} pts`
            }
          />
        )}
      </div>
    </>
  ) : (
    <PageLoading />
  );
}

export default connect(
  (state: ApplicationState) => ({
    toggleStatus: getToggleStatus(state),
    composition: getCompositionById(state),
    jsonStatsAsync: getCompositionJsonStatsAsync(state),
    isLoadingCompositionById: getIsLoadingCompositionById(state),
    isLoadingParentOrChildCompositionById:
      isLoadingParentOrChildCompositionById(state),
    parentOrChildComposition: getParentOrChildCompositionById(state),
    compositionFetchError: getCompositionFetchError(state),
    learningPathStep: getLearningPathStep(state),
    learningPath: getLearningPath(state),
  }),
  {
    fetchCompositionByIdRequest,
    fetchCompositionJsonStatsAsyncRequest,
    setCompositionLocalState,
    setCompositionEditorState,
    clearParentOrChildComposition,
    changeToggleCompletedTestReportStatus,
    fetchLearningPathStepByOrderRequest,
    setHighlight,
  },
)(ReviewedComposition);
